import { DEFAULT_REGION, getLanguage } from 'frontcore/i18n'
import { WEEKDAY } from 'frontcore/domain/dates/types'


export const DAYS_IN_A_WEEK = 7
export const HOURS_IN_A_DAY = 24

type datePrecision = 'seconds' | 'minutes'


// Based on https://stackoverflow.com/a/57102881
export const getWeekStart = () => {
  const language = window.navigator.language
  const region = language.split('-').at(-1)?.toUpperCase() ?? DEFAULT_REGION

  const regionSat = 'AEAFBHDJDZEGIQIRJOKWLYOMQASDSY'.match(/../g)
  const regionSun = 'AGARASAUBDBRBSBTBWBZCACNCODMDOETGTGUHKHNIDILINJMJPKEKHKRLAMHMMMOMTMXMZNINPPAPEPHPKPRPTPYSASGSVTHTTTWUMUSVEVIWSYEZAZW'.match(/../g)

  if (regionSun?.includes(region)) {
    return WEEKDAY.SUNDAY
  } else if (regionSat?.includes(region)) {
    return WEEKDAY.SATURDAY
  }

  return WEEKDAY.MONDAY
}

export const getWeekEnd = () => {
  const language = window.navigator.language
  const region = language.split('-').at(-1)?.toUpperCase() ?? DEFAULT_REGION

  const regionFri = 'AEAFBHDJDZEGIQIRJOKWLYOMQASDSY'.match(/../g)
  const regionSat = 'AGARASAUBDBRBSBTBWBZCACNCODMDOETGTGUHKHNIDILINJMJPKEKHKRLAMHMMMOMTMXMZNINPPAPEPHPKPRPTPYSASGSVTHTTTWUMUSVEVIWSYEZAZW'.match(/../g)

  if (regionSat?.includes(region)) {
    return WEEKDAY.SATURDAY
  } else if (regionFri?.includes(region)) {
    return WEEKDAY.FRIDAY
  }

  return WEEKDAY.SUNDAY
}

export const getWeekdayNames = () => {
  const weekdayNames: string[] = []
  const weekStart = getWeekStart()
  const firstDayOfTheYear = new Date(new Date().getFullYear(), 0, 1)
  firstDayOfTheYear.setDate(firstDayOfTheYear.getDate() + weekStart - firstDayOfTheYear.getDay())

  for (let weekDay=0; weekDay < DAYS_IN_A_WEEK; weekDay++) {
    const currentDate = new Date(new Date(firstDayOfTheYear).setDate(firstDayOfTheYear.getDate() + weekDay))
    const weekdayName = currentDate.toLocaleDateString(getLanguage(), {weekday: 'short'}).substring(0, 2)
    weekdayNames.push(weekdayName)
  }
  return weekdayNames
}

export const getWeekNumber = (date: Date) => {
  const milliseconsInAWeek = 604800000
  const weekStart = getWeekStart()
  const tdt = new Date(date.getTime())
  const offset = (tdt.getDay() + (DAYS_IN_A_WEEK - weekStart)) % 7
  tdt.setDate(tdt.getDate() - offset + WEEKDAY.THURSDAY - weekStart)
  const firstThursday = tdt.getTime()
  tdt.setMonth(0, 1)
  if (tdt.getDay() !== WEEKDAY.THURSDAY) {
    tdt.setMonth(0, 1 + ((WEEKDAY.THURSDAY - tdt.getDay()) + DAYS_IN_A_WEEK) % DAYS_IN_A_WEEK)
  }
  return 1 + Math.ceil((firstThursday - tdt.getTime()) / milliseconsInAWeek)
}

export const getStartOfWeek = (date: Date) => {
  const startOfWeek = new Date(date.getTime())
  const firstDayOfWeek = getWeekStart()
  const diff = (startOfWeek.getDay() < firstDayOfWeek ? 6 : 0) + startOfWeek.getDay() - firstDayOfWeek
  startOfWeek.setDate(startOfWeek.getDate() - diff)
  startOfWeek.setHours(0, 0, 0, 0)
  return startOfWeek
}

export const getEndOfWeek = (date: Date) => {
  const startOfWeek = getStartOfWeek(date)
  return new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + 6, 23, 59, 59, 999)
}

export const getStartOfMonth = (date: Date) => {
  const startOfMonth = new Date(date.getTime())
  startOfMonth.setFullYear(date.getFullYear(), date.getMonth(), 1)
  startOfMonth.setHours(0, 0, 0, 0)
  return startOfMonth
}

export const getEndOfMonth = (date: Date) => {
  const endOfMonth = new Date(date.getTime())
  endOfMonth.setFullYear(endOfMonth.getFullYear(), endOfMonth.getMonth() + 1, 0)
  endOfMonth.setHours(23, 59, 59, 999)
  return endOfMonth
}

export const getStartOfYear = (date: Date) => {
  const startOfYear = new Date(date.getTime())
  startOfYear.setFullYear(date.getFullYear(), 0, 1)
  startOfYear.setHours(0, 0, 0, 0)
  return startOfYear
}

export const getEndOfYear = (date: Date) => {
  const endOfYear = new Date(date.getTime())
  endOfYear.setFullYear(endOfYear.getFullYear(), 11, 31)
  endOfYear.setHours(23, 59, 59, 999)
  return endOfYear
}

export function dateSubtract (date: Date, {days, weeks, months}: Partial<Record< 'days' |'weeks' | 'months', number>>): Date {
  const pastDate = new Date(date)
  if (days !== undefined) {
    pastDate.setDate(date.getDate() - days)
  }
  if (weeks !== undefined) {
    pastDate.setDate(date.getDate() - (weeks * 7))
  }
  if (months !== undefined) {
    pastDate.setMonth(date.getMonth() - months)
  }

  return pastDate
}

export function dateIsAfter (questionDate: Date, targetDate: Date, precision: datePrecision | undefined = undefined) : boolean {
  return getDateWithPrecision(questionDate, precision).getTime() > getDateWithPrecision(targetDate, precision).getTime()
}

export function dateIsBefore (questionDate: Date, targetDate: Date, precision: datePrecision | undefined = undefined) : boolean {
  return getDateWithPrecision(questionDate, precision).getTime() < getDateWithPrecision(targetDate, precision).getTime()
}


function getDateWithPrecision (date: Date, precision: datePrecision | undefined = undefined) {
  const dateWithPrecision = new Date(date)
  switch (precision) {
  case "seconds":
    dateWithPrecision.setMilliseconds(0)
    break
  case "minutes":
    dateWithPrecision.setSeconds(0, 0)
  }
  return dateWithPrecision
}
